/**
 * Peg codes, mirroring internal/game/color.go and mode.go.
 *
 * A peg code is two characters: a colour letter followed by either the same
 * letter (a solid lamp, "rr") or a digit (a numbered lamp, "g5"). Declaration
 * order matters - Four takes the first four colours and Seventy walks the list
 * in order - so this array must stay in step with the Go side.
 */
export const COLORS = [
    { code: 'r', name: 'red', tenMode: false },
    { code: 'g', name: 'green', tenMode: true },
    { code: 'b', name: 'blue', tenMode: true },
    { code: 'y', name: 'yellow', tenMode: true },
    { code: 'p', name: 'pink', tenMode: true },
    { code: 'v', name: 'purple', tenMode: false },
    { code: 'w', name: 'white', tenMode: true },
];

const COLOR_CODES = COLORS.map((c) => c.code);

/** How many pegs make up a code. */
export const CODE_LENGTH = 5;

/**
 * Breaks a peg code into the pieces the template needs.
 *
 * Anything unrecognised comes back as an empty socket rather than throwing,
 * because these values arrive from Firestore snapshots.
 */
export function parsePeg(code) {
    const empty = { code: '', color: '', digit: '', solid: false, empty: true };
    if (typeof code !== 'string' || code.length !== 2) return empty;

    const color = code[0];
    const rest = code[1];
    if (!COLOR_CODES.includes(color)) return empty;

    if (rest === color) {
        return { code, color, digit: '', solid: true, empty: false };
    }
    if (rest >= '0' && rest <= '9') {
        return { code, color, digit: rest, solid: false, empty: false };
    }
    return empty;
}

/** Reports whether a peg code is well-formed. */
export function isValidPeg(code) {
    return !parsePeg(code).empty;
}

/** The CSS classes a peg element needs for the given code. */
export function pegClasses(code) {
    const peg = parsePeg(code);
    const classes = ['peg'];
    if (peg.empty) classes.push('peg--empty');
    else if (peg.solid) classes.push('peg--solid');
    return classes.join(' ');
}

/**
 * Renders a result the way the board shows it: placement first, then total.
 * The board reserves a fixed-width column for it, matched to the guess button,
 * so the rendered shape stays three characters.
 */
export function formatResult(result) {
    if (!result) return '0/0';
    const placement = Number(result.placement) || 0;
    const total = Number(result.total) || 0;
    return `${placement}/${total}`;
}

/**
 * What the second number of a score counts, one phrase per game.MatchKind.
 *
 * The classification is the game's - which modes score a peg on its digit is a
 * rule, and it is decided next to the code that compares pegs. The wording is
 * the board's, and belongs with the rest of the board's wording rather than
 * arriving as half a sentence from the server.
 */
const COUNTED = {
    color: 'the right colour',
    number: 'the right number',
    both: 'the right colour and number',
};

/** Anything unrecognised is counted as colour, as ParseMode falls back to Four. */
export function counted(matches) {
    return COUNTED[matches] || COUNTED.color;
}

/**
 * The two numbers as one line each, for a player meeting them for the first
 * time. Every score gets the same two lines: a score is a pair of counts, and
 * a reader learning to hold that shape is not helped by it being reworded
 * underneath them.
 *
 * Each line carries the whole score as well as its own words, because each one
 * is rendered led by the score with only its half lit - which is what ties the
 * words to the number above them.
 *
 * The misreading this exists to head off is that the counts are two separate
 * piles. A peg in the right place is also a peg of the right kind, so it is
 * counted in both - which is what the note carries. The note is worded to still
 * read when the two numbers are equal, since that is the case where a reader is
 * likeliest to take the second number for a second pile.
 *
 * `matches` is game.MatchKind: what this mode scores a peg on, which decides
 * what the second number can honestly be said to be counting.
 */
export function explainResult(result, matches) {
    const placement = Number(result && result.placement) || 0;
    const total = Number(result && result.total) || 0;
    const pegs = (n) => (n === 1 ? '1 peg' : `${n} pegs`);

    return [
        {
            focus: 'placement',
            placement,
            total,
            text: `${pegs(placement)} in the right place`,
            note: '',
        },
        {
            focus: 'total',
            placement,
            total,
            text: `${pegs(total)} ${counted(matches)}`,
            note: placement > 0 ? `counting the ${placement} above` : '',
        },
    ];
}
